import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle, Clock, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Incidents = ({ token }) => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchIncidents();
  }, []);

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/incidents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncidents(response.data);
    } catch (err) {
      setError('Failed to load incidents');
      console.error('Incidents error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getIncidentIcon = (alertType) => {
    switch (alertType) {
      case 'down':
        return AlertTriangle;
      case 'up':
        return CheckCircle;
      case 'slow':
        return Clock;
      default:
        return AlertTriangle;
    }
  };

  const getIncidentColor = (alertType) => {
    switch (alertType) {
      case 'down':
        return 'text-red-600 bg-red-50';
      case 'up':
        return 'text-green-600 bg-green-50';
      case 'slow':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDuration = (startTime, endTime) => {
    if (!endTime) return 'Ongoing';
    
    const duration = new Date(endTime) - new Date(startTime);
    const minutes = Math.floor(duration / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 xl:p-10">
        <div className="animate-pulse">
          <div className="space-y-4 lg:space-y-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-200 h-16 sm:h-20 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 xl:p-10">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Incidents</h2>
          <p className="text-gray-600 text-sm sm:text-base">Monitor downtime and outage history</p>
        </div>
        <button
          onClick={fetchIncidents}
          className="flex items-center justify-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm sm:text-base"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm sm:text-base">
          {error}
        </div>
      )}

      {incidents.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <div className="bg-green-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
          </div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No incidents reported</h3>
          <p className="text-gray-600 text-sm sm:text-base px-4">Your monitors are running smoothly! Incidents will appear here when downtime is detected.</p>
        </div>
      ) : (
        <div className="space-y-4 lg:space-y-6">
          {incidents.map((incident) => {
            const Icon = getIncidentIcon(incident.alert_type);
            const colorClass = getIncidentColor(incident.alert_type);
            
            return (
              <div key={incident.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className={`p-2 rounded-lg ${colorClass} flex-shrink-0`}>
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 space-y-2 sm:space-y-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                        {incident.alert_type === 'down' ? 'Website Down' :
                         incident.alert_type === 'up' ? 'Website Recovered' :
                         'Performance Issue'}
                      </h3>
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                        <span className={`px-3 py-1 text-xs sm:text-sm rounded-full ${
                          incident.status === 'open' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        } self-start`}>
                          {incident.status}
                        </span>
                        {incident.status === 'resolved' && (
                          <span className="text-xs sm:text-sm text-gray-500">
                            Duration: {formatDuration(incident.started_at, incident.resolved_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-3 text-sm sm:text-base break-words">{incident.message}</p>
                    
                    <div className="text-xs sm:text-sm text-gray-500 space-y-1">
                      <p>Started: {new Date(incident.started_at).toLocaleString()}</p>
                      {incident.resolved_at && (
                        <p>Resolved: {new Date(incident.resolved_at).toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Incidents;